import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ClerkProvider, SignedIn, SignedOut, SignIn } from '@clerk/clerk-react';
import FinancialStatementsForm from './FinancialStatementsForm';
import './App.css';

function App() {
  // Check if Clerk publishable key is available
  const clerkPublishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
  
  // Debug logging (remove in production)
  console.log('Environment check:', {
    hasKey: !!clerkPublishableKey,
    keyPrefix: clerkPublishableKey?.substring(0, 10) + '...',
    allEnvVars: Object.keys(import.meta.env)
  });
  
  // If no publishable key, show error message
  if (!clerkPublishableKey) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-lg mx-auto bg-white rounded-lg shadow-lg p-6">
          <div className="text-center">
            <div className="text-red-600 text-4xl mb-4">⚠️</div>
            <h1 className="text-2xl font-bold text-gray-800 mb-4">Configuration Error</h1>
            <p className="text-gray-600 mb-6">
              The Clerk publishable key is missing from environment variables.
            </p>
            <div className="bg-gray-100 p-4 rounded-md text-left mb-4">
              <p className="text-sm font-medium text-gray-700 mb-2">To fix this:</p>
              <ol className="text-sm text-gray-600 space-y-2">
                <li><strong>1. Local Development:</strong><br/>
                    Create <code className="bg-gray-200 px-1 rounded">.env</code> file with:<br/>
                    <code className="bg-gray-200 px-1 rounded text-xs">VITE_CLERK_PUBLISHABLE_KEY=pk_test_your_key</code>
                </li>
                <li><strong>2. Azure Deployment:</strong><br/>
                    Add environment variable in Azure Static Web Apps Configuration
                </li>
                <li><strong>3. Get your key:</strong><br/>
                    <a href="https://dashboard.clerk.com/last-active?path=api-keys" 
                       className="text-blue-600 hover:underline" 
                       target="_blank" 
                       rel="noopener noreferrer">
                      Clerk Dashboard → API Keys
                    </a>
                </li>
              </ol>
            </div>
            <button 
              onClick={() => window.location.reload()} 
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <ClerkProvider 
      publishableKey={clerkPublishableKey}
      appearance={{
        variables: {
          colorPrimary: '#5B4F8F',
          colorText: '#374151',
          colorTextSecondary: '#6B7280',
          colorBackground: '#ffffff',
          colorInputBackground: '#ffffff',
          colorInputText: '#374151',
          borderRadius: '0.5rem'
        }
      }}
    >
      <Router>
        <div className="min-h-screen bg-gray-50">
          <Routes>
            {/* Protected dashboard route */}
            <Route 
              path="/dashboard" 
              element={
                <SignedIn>
                  <FinancialStatementsForm />
                </SignedIn>
              } 
            />
            
            {/* Root redirect */}
            <Route 
              path="/" 
              element={
                <>
                  <SignedOut>
                    <Navigate to="/sign-in" replace />
                  </SignedOut>
                  <SignedIn>
                    <Navigate to="/dashboard" replace />
                  </SignedIn>
                </>
              } 
            />
            
            {/* Catch any sign-in paths that might slip through */}
            <Route 
              path="/sign-in/*" 
              element={
                <SignedIn>
                  <Navigate to="/dashboard" replace />
                </SignedIn>
              } 
            />
          </Routes>
          
          {/* Clerk components outside of Routes for full path control */}
          <SignedOut>
            <div className="min-h-screen flex items-center justify-center bg-white">
              <SignIn 
                routing="path"
                path="/sign-in"
                signUpUrl="https://finfile.techx.ae/sign-up"
                afterSignInUrl="https://finfile.techx.ae/dashboard"
                redirectUrl="https://finfile.techx.ae/dashboard"
                appearance={{
                  variables: {
                    colorPrimary: '#5B4F8F',
                    colorText: '#1F2937',
                    colorTextSecondary: '#6B7280',
                    colorBackground: '#ffffff',
                    colorInputBackground: '#ffffff',
                    colorInputText: '#1F2937',
                    borderRadius: '0.5rem'
                  },
                  elements: {
                    card: "shadow-lg border border-gray-200 rounded-lg",
                    formButtonPrimary: 'bg-[#5B4F8F] hover:bg-[#4a3f7a] text-white font-medium transition-colors',
                    footerActionLink: 'text-[#5B4F8F] hover:text-[#4a3f7a]',
                    formFieldInput: 'border-gray-300 focus:border-[#5B4F8F] focus:ring-[#5B4F8F]',
                    formFieldLabel: 'text-gray-700 font-medium'
                  }
                }}
              />
            </div>
          </SignedOut>
        </div>
      </Router>
    </ClerkProvider>
  );
}

export default App;